// reference: https://kentcdodds.com/blog/how-to-use-react-context-effectively
import React, { createContext, useReducer, useContext } from 'react'
import {
  imports,
  permissions,
  a8flowFunctions,
  defaultSource,
  a8AstroFunctions,
} from './constants'
import { getToggleData } from './helpers/common'
import {
  ExecutionMode,
  IToggleEntry,
  Context,
  ContextField,
  IEntry,
  PermissionField,
  IFunction,
  IMetadata,
} from './types'

export interface IState {
  source: string
  executionMode: ExecutionMode
  imports: IToggleEntry[]
  context: Context
  permissions: {
    a8config: IToggleEntry[]
    a8func: IToggleEntry[]
    a8flow: IToggleEntry[]
    a8Astro: IToggleEntry[]
  }
  metadata: IMetadata
  logs: any
}

interface IAction {
  type: string
  payload: any
}

interface IContext {
  state: IState
  dispatch: (action: IAction) => void
}

const CreateFunctionContext = createContext<IContext | undefined>(undefined)

const reducer = (state: IState, action: IAction) => {
  switch (action.type) {
    case 'SET_CONTEXT_ENTRY': {
      const {
        field,
        entry,
      }: {
        field: ContextField
        entry: IEntry
      } = action.payload
      const { key, value } = entry

      return {
        ...state,
        context: {
          ...state.context,
          [field]: [...state.context[field], [key, value]],
        },
      }
    }
    case 'DELETE_CONTEXT_ENTRY': {
      const {
        field,
        idx,
      }: {
        field: ContextField
        idx: number
      } = action.payload

      return {
        ...state,
        context: {
          ...state.context,
          [field]: state.context[field].filter((_, elIdx) => elIdx !== idx),
        },
      }
    }
    case 'EDIT_CONTEXT_ENTRY': {
      const {
        field,
        entry,
        idx,
      }: {
        field: ContextField
        entry: IEntry
        idx: number
      } = action.payload
      const { key, value } = entry

      return {
        ...state,
        context: {
          ...state.context,
          [field]: state.context[field].map((item, elIdx) =>
            elIdx !== idx ? item : [key, value]
          ),
        },
      }
    }
    case 'TOGGLE_IMPORT': {
      const idx = action.payload
      return {
        ...state,
        imports: state.imports.map((item, elIdx) =>
          idx === elIdx ? { ...item, isChecked: !item.isChecked } : item
        ),
      }
    }
    case 'SET_EXEC_MODE': {
      return {
        ...state,
        executionMode: action.payload,
      }
    }
    case 'TOGGLE_PERMISSION': {
      const { field, idx }: { field: PermissionField; idx: number } =
        action.payload
      return {
        ...state,
        permissions: {
          ...state.permissions,
          [field]: state.permissions[field].map((item, elIdx) =>
            idx === elIdx ? { ...item, isChecked: !item.isChecked } : item
          ),
        },
      }
    }
    case 'ADD_PERMISSION': {
      const { field, name }: { field: PermissionField; name: string } =
        action.payload
      return {
        ...state,
        permissions: {
          ...state.permissions,
          [field]: [...state.permissions[field], { name, isChecked: true }],
        },
      }
    }
    case 'MUTATE_FUNCTION': {
      const { type, data, idx } = action.payload

      if (type === 'add') {
        return {
          ...state,
          context: {
            ...state.context,
            functions: [...state.context.functions, data],
          },
        }
      }

      if (type === 'delete') {
        return {
          ...state,
          context: {
            ...state.context,
            functions: state.context.functions.filter(
              (_, elIdx) => elIdx !== idx
            ),
          },
        }
      }

      if (type === 'update') {
        return {
          ...state,
          context: {
            ...state.context,
            functions: state.context.functions.map((item, elIdx) =>
              elIdx === idx ? data : item
            ),
          },
        }
      }

      return state
    }
    case 'SET_STATE': {
      return action.payload
    }
    case 'ADD_LOGS': {
      return {
        ...state,
        logs: action.payload
      }
    }
    default:
      return state
  }
}

interface IProvider {
  children: React.ReactNode
}

const CreateFunctionProvider: React.FC<IProvider> = ({ children }) => {
  const [state, dispatch] = useReducer(reducer, {
    source: defaultSource,
    executionMode: 'client',
    context: {
      params: [],
      a8config: [],
      functions: [],
    },
    imports: getToggleData(imports, []),
    permissions: {
      a8config: getToggleData(permissions.a8config, []),
      a8func: [],
      a8flow: getToggleData(a8flowFunctions, []),
      a8Astro: getToggleData(a8AstroFunctions, []),
    },
    metadata: {
      id: '',
      name: '',
      description: '',
      preview_image: '',
      current_version: '',
      release_notes: '',
      author_website: '',
      support_email: '',
    },
  })

  const value = { state, dispatch }

  return (
    <CreateFunctionContext.Provider value={value}>
      {children}
    </CreateFunctionContext.Provider>
  )
}

const useCreateFunction = () => {
  const context = useContext(CreateFunctionContext)

  if (context === undefined) {
    throw new Error(
      'useCreateFunction must be used within a CreateFunctionProvider'
    )
  }

  return context
}

// actions
const setContextEntry = (payload: { entry: IEntry; field: ContextField }) => {
  return {
    type: 'SET_CONTEXT_ENTRY',
    payload,
  }
}

const deleteContextEntry = (payload: { field: ContextField; idx: number }) => {
  return {
    type: 'DELETE_CONTEXT_ENTRY',
    payload,
  }
}

const editContextEntry = (payload: {
  field: ContextField
  idx: number
  entry: IEntry
}) => {
  return {
    type: 'EDIT_CONTEXT_ENTRY',
    payload,
  }
}

const toggleImport = (payload: number) => {
  return {
    type: 'TOGGLE_IMPORT',
    payload,
  }
}

const togglePermission = (payload: { field: PermissionField; idx: number }) => {
  return {
    type: 'TOGGLE_PERMISSION',
    payload,
  }
}

const addPermission = (payload: { field: PermissionField; name: string }) => {
  return {
    type: 'ADD_PERMISSION',
    payload,
  }
}

const mutateFunction = (payload: {
  type: 'add' | 'delete' | 'update'
  data?: IFunction
  idx?: number
}) => {
  return {
    type: 'MUTATE_FUNCTION',
    payload,
  }
}

const setExecMode = (payload: ExecutionMode) => {
  return {
    type: 'SET_EXEC_MODE',
    payload,
  }
}

const setState = (payload: IState) => {
  return {
    type: 'SET_STATE',
    payload,
  }
}

const logs = (payload: any) => {
  return {
    type: 'ADD_LOGS',
    payload,
  }
}

export {
  CreateFunctionProvider,
  useCreateFunction,
  setContextEntry,
  deleteContextEntry,
  editContextEntry,
  toggleImport,
  togglePermission,
  addPermission,
  mutateFunction,
  setExecMode,
  setState,
  logs
}
